<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import GMCharSelectorCard from '@/components/GameMaster/Character/GMCharSelectorCard.vue';
import { CharacterGameApi } from '@/api/backendService';
import type { OpenaiCharacter } from '@/api/dx-backend';

const emit = defineEmits<{
  close: [];
  spawn: [character: OpenaiCharacter];
}>();

// State
const characters = ref<OpenaiCharacter[]>([]);
const loading = ref(false);
const error = ref('');
const selectedCharacter = ref<OpenaiCharacter | null>(null);

// Filter state
const searchQuery = ref('');
const selectedOrgId = ref('');
const selectedNpcFilter = ref('');
const selectedPositionId = ref('');

// Get unique organizations from characters
const organizations = computed(() => {
  const orgs = new Set<string>();
  characters.value.forEach(char => {
    if (char.organization?.id) {
      orgs.add(char.organization.id);
    }
  });
  return Array.from(orgs).map(id => {
    const char = characters.value.find(c => c.organization?.id === id);
    return {
      id,
      name: char?.organization?.name || `Organization ${id.slice(0, 8)}`
    };
  });
});

// Filtered characters based on search and filters
const filteredCharacters = computed(() => {
  let filtered = characters.value;

  // Apply search query (name, id, or position)
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim();
    filtered = filtered.filter(char =>
      char.name.toLowerCase().includes(query) ||
      char.id.toLowerCase().includes(query) ||
      char.position_id?.toLowerCase().includes(query) ||
      char.rank?.name?.toLowerCase().includes(query)
    );
  }

  // Apply organization filter
  if (selectedOrgId.value) {
    filtered = filtered.filter(char => char.organization?.id === selectedOrgId.value);
  }

  // Apply position filter
  if (selectedPositionId.value) {
    filtered = filtered.filter(char => char.position_id === selectedPositionId.value);
  }

  // Apply NPC filter
  if (selectedNpcFilter.value !== '') {
    const isNpc = selectedNpcFilter.value === 'true';
    filtered = filtered.filter(char => char.npc === isNpc);
  }

  return filtered;
});

// Active filters computed property
const activeFilters = computed(() => {
  const filters = [];

  if (searchQuery.value.trim()) {
    filters.push({
      type: 'search',
      label: `Search: "${searchQuery.value}"`,
      value: searchQuery.value
    });
  }

  if (selectedOrgId.value) {
    const org = organizations.value.find(o => o.id === selectedOrgId.value);
    filters.push({
      type: 'organization',
      label: `Organization: ${org?.name || 'Unknown'}`,
      value: selectedOrgId.value
    });
  }

  if (selectedNpcFilter.value !== '') {
    const label = selectedNpcFilter.value === 'true' ? 'NPCs Only' : 'Players Only';
    filters.push({
      type: 'npc',
      label: label,
      value: selectedNpcFilter.value
    });
  }

  if (selectedPositionId.value) {
    filters.push({
      type: 'position',
      label: `Position: ${selectedPositionId.value.slice(0, 8)}...`,
      value: selectedPositionId.value
    });
  }

  return filters;
});

// Clear all filters function
const clearAllFilters = () => {
  searchQuery.value = '';
  selectedOrgId.value = '';
  selectedNpcFilter.value = '';
  selectedPositionId.value = '';
};

// Individual filter removal functions
const removeFilter = (filterType: string) => {
  switch (filterType) {
    case 'search':
      searchQuery.value = '';
      break;
    case 'organization':
      selectedOrgId.value = '';
      break;
    case 'npc':
      selectedNpcFilter.value = '';
      break;
    case 'position':
      selectedPositionId.value = '';
      break;
  }
};

// Fast action handlers
const copyCharacterId = (characterId: string) => {
  navigator.clipboard.writeText(characterId);
};

const filterByCharacterPosition = (character: OpenaiCharacter) => {
  if (character.position_id) {
    selectedPositionId.value = character.position_id;
  }
};

const filterByCharacterOrganisation = (character: OpenaiCharacter) => {
  if (character.organization?.id) {
    selectedOrgId.value = character.organization.id;
  }
};

// Load characters
async function loadCharacters() {
  loading.value = true;
  error.value = '';

  try {
    console.log('[CharacterSpawner] Loading characters from campaign...');
    const response = await CharacterGameApi.characterGmList();
    characters.value = response.data.results || response.data || [];
    console.log('[CharacterSpawner] ✅ Loaded characters:', characters.value.length);
  } catch (err: any) {
    console.error('[CharacterSpawner] ❌ Error loading characters:', err);
    error.value = 'Failed to load characters';
  } finally {
    loading.value = false;
  }
}

// Select character
function selectCharacter(character: OpenaiCharacter) {
  selectedCharacter.value = character;
}

// Spawn character
function handleSpawn() {
  if (selectedCharacter.value) {
    emit('spawn', selectedCharacter.value);
  }
}

onMounted(() => {
  loadCharacters();
});
</script>

<template>
  <div class="gm-panel gm-panel--raised char-spawner-container">
    <div class="header">
      <div class="header-top">
        <h2 class="gm-title title">Spawn Character</h2>
        <button @click="emit('close')" class="close-btn" title="Close Character Spawner">
          ×
        </button>
      </div>

      <!-- Filter Controls -->
      <div class="filters">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search characters..."
          class="gm-input search-input"
        />
        <select v-model="selectedNpcFilter" class="gm-select filter-select">
          <option value="">All Characters</option>
          <option value="false">Players Only</option>
          <option value="true">NPCs Only</option>
        </select>
        <select v-model="selectedOrgId" class="gm-select filter-select">
          <option value="">All Organizations</option>
          <option
            v-for="org in organizations"
            :key="org.id"
            :value="org.id"
          >
            {{ org.name }}
          </option>
        </select>
      </div>

      <!-- Active Filters -->
      <div v-if="activeFilters.length > 0" class="active-filters">
        <span class="gm-label filter-label">Active Filters:</span>
        <div class="filter-chips">
          <span
            v-for="filter in activeFilters"
            :key="filter.type"
            class="gm-chip filter-chip"
            @click="removeFilter(filter.type)"
          >
            {{ filter.label }}
            <button class="chip-remove" title="Remove Filter">×</button>
          </span>
          <button class="gm-btn gm-btn--sm gm-btn--danger clear-all-btn" @click="clearAllFilters" title="Clear All Filters">
            Clear All
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="gm-empty loading-state">
      <span class="dx-loader" aria-hidden="true"></span>
      <p class="gm-empty__hint">Loading characters...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="gm-empty error-state">
      <p class="error-message">{{ error }}</p>
      <button class="gm-btn retry-btn" @click="loadCharacters">Retry</button>
    </div>

    <!-- Character Grid -->
    <div v-else class="character-grid gm-scroll">
      <div
        v-for="character in filteredCharacters"
        :key="character.id"
        @click="selectCharacter(character)"
      >
        <GMCharSelectorCard
          :character="character"
          :selected="selectedCharacter?.id === character.id"
          @copy-id="copyCharacterId"
          @filter-by-position="filterByCharacterPosition"
          @filter-by-organisation="filterByCharacterOrganisation"
        />
      </div>

      <!-- Empty State -->
      <div v-if="filteredCharacters.length === 0" class="gm-empty empty-state">
        <p class="gm-empty__title">No characters found</p>
        <button class="gm-btn clear-filters-btn" @click="clearAllFilters">
          Clear Filters
        </button>
      </div>
    </div>

    <!-- Actions -->
    <div class="actions">
      <button class="gm-btn gm-btn--ghost cancel-btn" @click="emit('close')">Cancel</button>
      <button
        class="gm-btn gm-btn--primary spawn-btn"
        :disabled="!selectedCharacter"
        @click="handleSpawn"
      >
        Spawn Character
      </button>
    </div>
  </div>
</template>

<style scoped>
.char-spawner-container {
  width: 56.25rem;
  max-width: 95vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.header {
  margin-bottom: var(--gm-gap);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap);
}

.title {
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  color: var(--gm-text-muted);
  font-size: 1.75rem;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s ease;
}

.close-btn:hover {
  color: var(--gm-accent);
}

.filters {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: var(--gm-gap-sm);
  margin-bottom: var(--gm-gap-sm);
}

.active-filters {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  flex-wrap: wrap;
  padding: var(--gm-gap-sm);
  background: var(--gm-surface-sunken);
  border-radius: var(--gm-radius-sm);
}

.filter-chips {
  display: flex;
  gap: var(--gm-gap-sm);
  flex-wrap: wrap;
  flex: 1;
}

.filter-chip {
  cursor: pointer;
}

.chip-remove {
  background: none;
  border: none;
  color: inherit;
  font-size: 1rem;
  padding: 0;
  cursor: pointer;
  line-height: 1;
}

.loading-state,
.error-state {
  padding: 3rem;
}

.error-message {
  color: var(--gm-danger);
  margin-bottom: var(--gm-gap);
  font-size: 1rem;
}

.character-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(8.75rem, 1fr));
  gap: var(--gm-gap);
  padding: var(--gm-gap-sm);
  margin-bottom: var(--gm-gap);
}

.empty-state {
  grid-column: 1 / -1;
  padding: 3rem;
}

.clear-filters-btn {
  margin-top: var(--gm-gap);
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--gm-gap-sm);
  padding-top: var(--gm-gap);
  border-top: 1px solid var(--gm-border-dim);
}
</style>
